import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Volume2, RotateCcw, Globe, Plus, Camera, Delete, Sparkles, HeartPulse, Apple, Smile, BookOpen, Layers } from 'lucide-react';
import { CustomTileModal, CustomAACTile } from './CustomTileModal';
import { touchDwellEngine } from '../utils/touchDwellEngine';
import { getVerbInflections, getNounInflections, GrammarInflection } from '../utils/aacGrammarEngine';

export interface StandardAACTile {
  id: string;
  label: string;
  emoji: string;
  category: 'pronoun' | 'verb' | 'noun' | 'adjective' | 'social';
  imageUri?: string;
  audioBlobUrl?: string;
}

export type VocabularyCategory = 'core' | 'medical' | 'sensory' | 'food';

const INITIAL_CORE_TILES: StandardAACTile[] = [
  // Pronouns (Yellow)
  { id: 'p1', label: 'I / ME', emoji: '🙋‍♂️', category: 'pronoun' },
  { id: 'p2', label: 'YOU', emoji: '👉', category: 'pronoun' },
  { id: 'p3', label: 'HE', emoji: '👦', category: 'pronoun' },
  { id: 'p4', label: 'SHE', emoji: '👧', category: 'pronoun' },
  { id: 'p5', label: 'THEY', emoji: '👥', category: 'pronoun' },

  // Verbs (Green)
  { id: 'v1', label: 'WANT', emoji: '🤲', category: 'verb' },
  { id: 'v2', label: 'LIKE', emoji: '👍', category: 'verb' },
  { id: 'v3', label: 'PLAY', emoji: '🎮', category: 'verb' },
  { id: 'v4', label: 'GO', emoji: '🚶', category: 'verb' },
  { id: 'v5', label: 'HAVE', emoji: '🤝', category: 'verb' },

  // Nouns / Objects (Orange)
  { id: 'n1', label: 'WATER', emoji: '💧', category: 'noun' },
  { id: 'n2', label: 'FOOD', emoji: '🍎', category: 'noun' },
  { id: 'n3', label: 'BOOK', emoji: '📖', category: 'noun' },
  { id: 'n4', label: 'TOY', emoji: '🧸', category: 'noun' },
  { id: 'n5', label: 'HOME', emoji: '🏠', category: 'noun' },

  // Adjectives / Descriptors (Blue) & Social (Pink)
  { id: 'a1', label: 'HELP', emoji: '🆘', category: 'social' },
  { id: 'a2', label: 'STOP', emoji: '🛑', category: 'social' },
  { id: 'a3', label: 'HAPPY', emoji: '😊', category: 'adjective' },
  { id: 'a4', label: 'TIRED', emoji: '😴', category: 'adjective' },
  { id: 'a5', label: 'EAT', emoji: '🍽️', category: 'verb' }
];

const MEDICAL_PAIN_TILES: StandardAACTile[] = [
  { id: 'm1', label: 'HURTS', emoji: '🩹', category: 'social' },
  { id: 'm2', label: 'HEADACHE', emoji: '🤕', category: 'noun' },
  { id: 'm3', label: 'STOMACH', emoji: '🤢', category: 'noun' },
  { id: 'm4', label: 'MEDICINE', emoji: '💊', category: 'noun' },
  { id: 'm5', label: 'DOCTOR', emoji: '🩺', category: 'pronoun' },
  { id: 'm6', label: 'HOT / FEVER', emoji: '🤒', category: 'adjective' },
  { id: 'm7', label: 'COLD / CHILLS', emoji: '🥶', category: 'adjective' },
  { id: 'm8', label: 'ITCHY', emoji: '🐜', category: 'adjective' },
  { id: 'm9', label: 'TIRED / WEAK', emoji: '🥱', category: 'adjective' },
  { id: 'm10', label: 'BATHROOM', emoji: '🚻', category: 'noun' },
];

const SENSORY_REGULATION_TILES: StandardAACTile[] = [
  { id: 's1', label: 'TOO LOUD', emoji: '📢', category: 'adjective' },
  { id: 's2', label: 'TOO BRIGHT', emoji: '💡', category: 'adjective' },
  { id: 's3', label: 'NEED QUIET', emoji: '🤫', category: 'social' },
  { id: 's4', label: 'DEEP PRESSURE', emoji: '🫂', category: 'verb' },
  { id: 's5', label: 'SENSORY BREAK', emoji: '🧘', category: 'social' },
  { id: 's6', label: 'OVERWHELMED', emoji: '🤯', category: 'adjective' },
  { id: 's7', label: 'HEADPHONES', emoji: '🎧', category: 'noun' },
  { id: 's8', label: 'WEIGHTED VEST', emoji: '🦺', category: 'noun' },
  { id: 's9', label: 'CALM DOWN', emoji: '✨', category: 'verb' },
  { id: 's10', label: 'CHEWY TOY', emoji: '🦷', category: 'noun' },
];

const FOOD_NUTRITION_TILES: StandardAACTile[] = [
  { id: 'f1', label: 'WATER', emoji: '💧', category: 'noun' },
  { id: 'f2', label: 'ROTI / CHAPATI', emoji: '🫓', category: 'noun' },
  { id: 'f3', label: 'RICE', emoji: '🍚', category: 'noun' },
  { id: 'f4', label: 'DAL / LENTILS', emoji: '🍲', category: 'noun' },
  { id: 'f5', label: 'MILK', emoji: '🥛', category: 'noun' },
  { id: 'f6', label: 'FRUIT / APPLE', emoji: '🍎', category: 'noun' },
  { id: 'f7', label: 'SNACK / BISCUIT', emoji: '🍪', category: 'noun' },
  { id: 'f8', label: 'HUNGRY', emoji: '😋', category: 'adjective' },
  { id: 'f9', label: 'THIRSTY', emoji: '🥤', category: 'adjective' },
  { id: 'f10', label: 'MORE PLEASE', emoji: '➕', category: 'social' },
];

export const AACBoard: React.FC = () => {
  const { i18n } = useTranslation();
  const [activeCategory, setActiveCategory] = useState<VocabularyCategory>('core');
  const [selectedPhrase, setSelectedPhrase] = useState<StandardAACTile[]>([]);
  const [customTiles, setCustomTiles] = useState<StandardAACTile[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [language, setLanguage] = useState('en-US');
  const [activeInflections, setActiveInflections] = useState<{ parentTile: StandardAACTile; inflections: GrammarInflection[] } | null>(null);

  useEffect(() => {
    if (i18n.language === 'hi') setLanguage('hi-IN');
    else if (i18n.language === 'mr') setLanguage('mr-IN');
    else setLanguage('en-US');
  }, [i18n.language]);

  const getCurrentTiles = (): StandardAACTile[] => {
    let baseList: StandardAACTile[] = [];
    if (activeCategory === 'core') baseList = INITIAL_CORE_TILES;
    else if (activeCategory === 'medical') baseList = MEDICAL_PAIN_TILES;
    else if (activeCategory === 'sensory') baseList = SENSORY_REGULATION_TILES;
    else if (activeCategory === 'food') baseList = FOOD_NUTRITION_TILES;

    return [...customTiles.filter(t => activeCategory === 'core' || t.category === activeCategory), ...baseList];
  };

  const handleTileDirectOrDwell = (tile: StandardAACTile, e: React.PointerEvent) => {
    touchDwellEngine.handlePointerDown(e, () => {
      handleSelectSymbol(tile);
    });
  };

  const handleSelectSymbol = (tile: StandardAACTile) => {
    setSelectedPhrase((prev) => [...prev, tile]);
    speakTileAudio(tile);

    // If verb or noun, trigger grammar morphology options
    if (tile.category === 'verb') {
      const inflections = getVerbInflections(tile.label);
      setActiveInflections({ parentTile: tile, inflections });
    } else if (tile.category === 'noun') {
      const inflections = getNounInflections(tile.label);
      setActiveInflections({ parentTile: tile, inflections });
    } else {
      setActiveInflections(null);
    }
  };

  const handleApplyInflection = (inflectionLabel: string) => {
    if (!activeInflections) return;
    const modifiedTile: StandardAACTile = {
      ...activeInflections.parentTile,
      label: inflectionLabel
    };

    // Replace last tile in phrase
    setSelectedPhrase(prev => {
      if (prev.length === 0) return [modifiedTile];
      const copy = [...prev];
      copy[copy.length - 1] = modifiedTile;
      return copy;
    });

    speakTileAudio(modifiedTile);
    setActiveInflections(null);
  };

  const speakTileAudio = (tile: StandardAACTile) => {
    if (tile.audioBlobUrl) {
      const a = new Audio(tile.audioBlobUrl);
      a.play();
      return;
    }

    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(tile.label);
      u.lang = language;
      u.rate = 0.9;
      window.speechSynthesis.speak(u);
    }
  };

  const handleSpeakSentence = () => {
    if (selectedPhrase.length === 0) return;
    const text = selectedPhrase.map(t => t.label).join(' ');
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.lang = language;
      u.rate = 0.85;
      window.speechSynthesis.speak(u);
    }
  };

  const handleBackspace = () => {
    setSelectedPhrase(prev => prev.slice(0, prev.length - 1));
    setActiveInflections(null);
  };

  const handleClear = () => {
    setSelectedPhrase([]);
    setActiveInflections(null);
    if ('speechSynthesis' in window) window.speechSynthesis.cancel();
  };

  const handleSaveCustomTile = (newTile: CustomAACTile) => {
    const tileFormatted: StandardAACTile = {
      id: newTile.id,
      label: newTile.label,
      emoji: newTile.icon || '⭐',
      category: newTile.category,
      imageUri: newTile.imageUri,
      audioBlobUrl: newTile.audioBlobUrl
    };
    setCustomTiles(prev => [tileFormatted, ...prev]);
  };

  // Fitzgerald Key Category Styling
  const getCategoryStyles = (category: StandardAACTile['category']) => {
    switch (category) {
      case 'pronoun':
        return 'bg-[#ffd64e] text-slate-900 border-[#ffd64e]/60 hover:bg-amber-300';
      case 'verb':
        return 'bg-emerald-500 text-white border-emerald-600 hover:bg-emerald-600';
      case 'noun':
        return 'bg-orange-400 text-white border-orange-500 hover:bg-orange-500';
      case 'adjective':
        return 'bg-sky-400 text-white border-sky-500 hover:bg-sky-500';
      case 'social':
        return 'bg-pink-400 text-white border-pink-500 hover:bg-pink-500';
      default:
        return 'bg-slate-100 text-slate-800 border-slate-300';
    }
  };

  return (
    <div className="p-4 sm:p-6 bg-[#fbf9f5] rounded-[36px] shadow-lg border border-black/10 space-y-4">
      
      {/* 0. CATEGORY PACK SELECTOR (Avaz & TD Snap Standard) */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-black/5 pb-3">
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            onClick={() => { setActiveCategory('core'); setActiveInflections(null); }}
            className={`px-4 py-2 rounded-full font-black text-xs transition flex items-center gap-1.5 cursor-pointer ${
              activeCategory === 'core'
                ? 'bg-[#1c1b1f] text-[#ffd64e] shadow-sm'
                : 'bg-white text-slate-700 hover:bg-slate-100 border border-black/10'
            }`}
          >
            <Layers className="w-3.5 h-3.5" />
            <span>Core Vocab (LAMP)</span>
          </button>

          <button
            onClick={() => { setActiveCategory('medical'); setActiveInflections(null); }}
            className={`px-4 py-2 rounded-full font-black text-xs transition flex items-center gap-1.5 cursor-pointer ${
              activeCategory === 'medical'
                ? 'bg-rose-600 text-white shadow-sm'
                : 'bg-white text-slate-700 hover:bg-rose-50 border border-black/10'
            }`}
          >
            <HeartPulse className="w-3.5 h-3.5" />
            <span>Medical & Pain Localization</span>
          </button>

          <button
            onClick={() => { setActiveCategory('sensory'); setActiveInflections(null); }}
            className={`px-4 py-2 rounded-full font-black text-xs transition flex items-center gap-1.5 cursor-pointer ${
              activeCategory === 'sensory'
                ? 'bg-purple-600 text-white shadow-sm'
                : 'bg-white text-slate-700 hover:bg-purple-50 border border-black/10'
            }`}
          >
            <Smile className="w-3.5 h-3.5" />
            <span>Sensory & Regulation</span>
          </button>

          <button
            onClick={() => { setActiveCategory('food'); setActiveInflections(null); }}
            className={`px-4 py-2 rounded-full font-black text-xs transition flex items-center gap-1.5 cursor-pointer ${
              activeCategory === 'food'
                ? 'bg-amber-600 text-white shadow-sm'
                : 'bg-white text-slate-700 hover:bg-amber-50 border border-black/10'
            }`}
          >
            <Apple className="w-3.5 h-3.5" />
            <span>Daily Nutrition & Foods</span>
          </button>
        </div>

        <button
          onClick={() => setIsModalOpen(true)}
          data-switch-item="true"
          className="px-4 py-2 rounded-full bg-[#ffd64e] hover:bg-amber-300 text-[#1c1b1f] font-black text-xs flex items-center gap-1.5 transition shadow cursor-pointer switch-target"
          title="Create Custom Symbol Tile"
        >
          <Camera className="w-3.5 h-3.5" />
          <span>+ Add Custom Tile</span>
        </button>
      </div>

      {/* 1. TOP SENTENCE ACCUMULATION WINDOW (Proloquo & TD Snap Standard) */}
      <div className="bg-white rounded-[28px] border-2 border-black/10 p-3 sm:p-4 shadow-sm flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
        
        {/* Sentence Strip Tiles */}
        <div 
          className="flex-1 min-h-[56px] p-2 bg-[#f9f6f0] rounded-2xl border border-black/5 flex items-center gap-2 overflow-x-auto"
          role="status"
          aria-live="polite"
        >
          {selectedPhrase.length === 0 ? (
            <span className="text-xs text-slate-400 italic px-2 font-medium">
              Tap symbols to construct sentence (e.g., I + WANT + WATER)...
            </span>
          ) : (
            selectedPhrase.map((tile, idx) => (
              <span 
                key={idx} 
                className={`px-3 py-1.5 rounded-xl font-black text-xs shadow-xs flex items-center gap-1.5 shrink-0 ${getCategoryStyles(tile.category)}`}
              >
                <span>{tile.emoji}</span>
                <span>{tile.label}</span>
              </span>
            ))
          )}
        </div>

        {/* Action Controls */}
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={handleSpeakSentence}
            disabled={selectedPhrase.length === 0}
            data-switch-item="true"
            className="px-6 py-3 rounded-2xl bg-[#ffd64e] hover:bg-amber-400 disabled:opacity-40 text-black font-black text-xs uppercase tracking-wider transition-all shadow-md flex items-center gap-2 cursor-pointer switch-target"
            title="Speak Full Sentence"
          >
            <Volume2 className="w-4 h-4 fill-current" />
            <span>SPEAK</span>
          </button>

          <button
            onClick={handleBackspace}
            disabled={selectedPhrase.length === 0}
            data-switch-item="true"
            className="p-3 rounded-2xl bg-white hover:bg-slate-100 disabled:opacity-30 text-slate-700 border border-black/10 transition cursor-pointer switch-target"
            title="Backspace Last Word"
          >
            <Delete className="w-4 h-4" />
          </button>

          <button
            onClick={handleClear}
            disabled={selectedPhrase.length === 0}
            data-switch-item="true"
            className="p-3 rounded-2xl bg-white hover:bg-rose-50 disabled:opacity-30 text-rose-600 border border-black/10 transition cursor-pointer switch-target"
            title="Clear All"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* 1.5. GRAMMAR & INFLECTION RIBBON (Proloquo2Go Parity) */}
      {activeInflections && (
        <div className="p-3 bg-amber-50 rounded-2xl border border-amber-200 flex flex-wrap items-center gap-2 animate-fade-in">
          <span className="text-[11px] font-black uppercase text-amber-900 flex items-center gap-1">
            <Sparkles className="w-3.5 h-3.5 text-amber-600" />
            <span>Grammar Tenses for "{activeInflections.parentTile.label}":</span>
          </span>
          <div className="flex flex-wrap items-center gap-1.5">
            {activeInflections.inflections.map((inf, i) => (
              <button
                key={i}
                onClick={() => handleApplyInflection(inf.label)}
                className="px-3 py-1 bg-white hover:bg-[#ffd64e] text-slate-900 border border-amber-300 rounded-xl text-xs font-black transition cursor-pointer shadow-xs"
              >
                {inf.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 2. ERGONOMIC FITZGERALD KEY COMMUNICATION GRID */}
      <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 gap-2.5 sm:gap-3.5">
        {getCurrentTiles().map((tile) => (
          <button
            key={tile.id}
            onPointerDown={(e) => handleTileDirectOrDwell(tile, e)}
            data-switch-item="true"
            className={`min-h-[72px] sm:min-h-[88px] p-3 sm:p-4 rounded-[24px] border-2 transition-all flex flex-col items-center justify-center gap-1.5 cursor-pointer transform active:scale-95 shadow-xs switch-target ${getCategoryStyles(tile.category)}`}
          >
            {tile.imageUri ? (
              <img src={tile.imageUri} alt={tile.label} className="w-9 h-9 object-cover rounded-xl shadow-xs" />
            ) : (
              <span className="text-2xl sm:text-3xl filter drop-shadow-xs">{tile.emoji}</span>
            )}
            <span className="font-black text-xs tracking-tight text-center leading-tight">
              {tile.label}
            </span>
          </button>
        ))}
      </div>

      {/* Modal: Custom Photo & Audio Recording Tile Builder */}
      <CustomTileModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveCustomTile}
      />

    </div>
  );
};
